import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { ReactNode } from 'react'

import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Meter,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import { billingApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { formatBytes, formatDate, formatLimit, formatMoney, formatSeats, titleCase } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useBillingSummary } from '@/lib/queries'
import { useWorkspace } from '@/lib/workspace'
import type { BillingInterval, BillingPlanEntry, BillingSummary, PlanStatus } from '@/lib/types'

const STATUS_TONE: Record<string, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  free: 'neutral',
  trialing: 'brand',
  active: 'success',
  past_due: 'warning',
  canceled: 'danger',
}

/**
 * `/{slug}/settings/billing` — plan, seats, usage, and the two buttons that
 * matter: upgrade (hosted Checkout) and manage (hosted Portal).
 *
 * Every price and seat cap on this screen comes from the API's plan catalogue.
 * Nothing here hardcodes a number: the ladder is a server fact, and a page that
 * invented one would quietly disagree with what Stripe actually charges.
 *
 * Neither button ever handles a card. Both ask the API for a hosted URL and
 * hand the browser over, so no payment detail passes through this app. On a
 * deployment with placeholder Stripe keys the server returns a simulated URL
 * and says so, which is what makes the whole flow runnable locally.
 */
export default function BillingPage() {
  const workspace = useWorkspace()
  const queryClient = useQueryClient()
  const summary = useBillingSummary(workspace.slug)
  const [interval, setInterval] = useState<BillingInterval>('year')

  const checkout = useMutation({
    mutationFn: (plan: string) => billingApi.checkout(workspace.slug, { plan, interval }),
    onSuccess: (session) => {
      window.location.assign(session.url)
    },
  })

  const portal = useMutation({
    mutationFn: () => billingApi.portal(workspace.slug),
    onSuccess: (session) => {
      window.location.assign(session.url)
    },
  })

  const syncSeats = useMutation({
    mutationFn: () => billingApi.syncSeats(workspace.slug),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.billingSummary(workspace.slug) }),
  })

  if (summary.isPending) {
    return (
      <Card>
        <CardHeader title="Billing" />
        <div className="p-5">
          <SkeletonRows rows={4} />
        </div>
      </Card>
    )
  }

  if (summary.isError) {
    return (
      <Alert tone="danger" title="Billing is unavailable">
        {messageFor(summary.error)}
      </Alert>
    )
  }

  const data: BillingSummary = summary.data
  const { entitlements: ent, usage } = data
  const simulated = data.provider !== 'stripe'
  const canBill = workspace.can('org.billing')

  // The current plan may not be one of the four on sale: an App Store
  // subscription resolves to "pro", which is granted rather than sold.
  const currentPlan = data.plans.find((plan) => plan.id === ent.plan)

  // Unlimited differs from Business only in storage headroom a solo workspace
  // will not reach, so it buys one person nothing. It stays visible if they are
  // on it.
  const personalPlans = workspace.isPersonal
    ? data.plans.filter((plan) => plan.id !== 'unlimited' || plan.id === ent.plan)
    : data.plans
  const currentPlanName = currentPlan?.name ?? (ent.pro ? titleCase(ent.plan) : 'Free')
  const seatLimit = ent.limits.members

  return (
    <div className="space-y-6">
      {simulated ? (
        <Alert tone="warning" title="Test billing">
          This deployment has no live Stripe credentials, so checkout and the portal open a
          simulated page instead of charging anything.
        </Alert>
      ) : null}

      {data.managed_by_apple ? (
        <Alert tone="info" title="Managed through the App Store">
          This workspace is Pro because of an App Store subscription
          {ent.apple_product_id ? ` (${ent.apple_product_id})` : ''}. Change or cancel it in
          Settings on your iPad — paying here as well would bill you twice.
        </Alert>
      ) : null}

      {ent.in_grace ? (
        <Alert tone="warning" title="Payment problem">
          The last payment did not go through. Pro stays on until{' '}
          {formatDate(ent.expires_at)} — update the card to keep it.
        </Alert>
      ) : null}

      {ent.cancel_at_period_end ? (
        <Alert tone="warning" title="Cancels at the end of the period">
          Pro remains active until {formatDate(ent.expires_at)}, then this workspace returns to
          Free.
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Current plan"
          action={
            <Badge tone={STATUS_TONE[ent.status as PlanStatus] ?? 'neutral'}>
              {titleCase(ent.status || ent.plan)}
            </Badge>
          }
        />
        <div className="grid gap-5 p-5 sm:grid-cols-2">
          <div>
            <p className="text-2xl font-semibold tracking-tight">{currentPlanName}</p>
            <p className={cn('mt-1 text-sm', mutedTextClass)}>
              {ent.pro
                ? `Billed through ${titleCase(ent.source)}${ent.expires_at ? ` · renews ${formatDate(ent.expires_at)}` : ''}`
                : 'Upgrade for more seats, sprints, reports and unlimited projects.'}
            </p>
            <p className={cn('mt-1 text-sm', mutedTextClass)}>
              {formatSeats(seatLimit)} included
              {!currentPlan && ent.pro ? ' · not sold on the web' : ''}
            </p>
            {ent.trial_end ? (
              <p className={cn('mt-1 text-sm', mutedTextClass)}>
                Trial ends {formatDate(ent.trial_end)}.
              </p>
            ) : null}
          </div>

          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className={mutedTextClass}>Seats used</dt>
              <dd className="font-medium tabular-nums">
                {ent.member_count} of {formatLimit(seatLimit)}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className={mutedTextClass}>Billable seats</dt>
              <dd className="font-medium tabular-nums">{ent.billable_seats}</dd>
            </div>
            {ent.subscription_status ? (
              <div className="flex justify-between gap-3">
                <dt className={mutedTextClass}>Subscription</dt>
                <dd className="font-medium">{titleCase(ent.subscription_status)}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        <div className="flex flex-wrap gap-2 border-t border-ink-200 px-5 py-4 dark:border-ink-800">
          {data.can_manage ? (
            <Button variant="secondary" loading={portal.isPending} onClick={() => portal.mutate()}>
              Manage subscription
            </Button>
          ) : null}
          {canBill ? (
            <Button size="md" loading={syncSeats.isPending} onClick={() => syncSeats.mutate()}>
              Sync seats
            </Button>
          ) : null}
          {!canBill ? (
            <p className={cn('text-sm', mutedTextClass)}>
              Only the workspace owner can change billing.
            </p>
          ) : null}
        </div>

        {portal.error ? (
          <div className="px-5 pb-4">
            <Alert tone="danger">{messageFor(portal.error)}</Alert>
          </div>
        ) : null}
        {syncSeats.isSuccess ? (
          <div className="px-5 pb-4">
            <Alert tone="success">Seat count synced: {syncSeats.data.seats}.</Alert>
          </div>
        ) : null}
      </Card>

      <Card>
        <CardHeader
          title="Usage"
          description={
            usage.counted_since
              ? `Counted from ${formatDate(usage.counted_since)}; anything older is grandfathered.`
              : undefined
          }
        />
        <div className="grid gap-5 p-5 sm:grid-cols-2">
          <Meter label="Projects" used={usage.projects} limit={ent.limits.projects} />
          <Meter label="Issues" used={usage.issues} limit={ent.limits.issues} />
          <Meter label="Notes" used={usage.notes} limit={ent.limits.notes} />
          <Meter label="Seats" used={usage.members} limit={ent.limits.members} />
          <Meter
            label="Attachments"
            used={usage.storage_bytes}
            limit={ent.limits.attachment_bytes}
            formatValue={formatBytes}
          />
          <div>
            <p className="text-sm font-medium">Included</p>
            <ul className="mt-2 space-y-1 text-xs">
              <Included on={ent.limits.sprints}>Sprints</Included>
              <Included on={ent.limits.reports}>Reports</Included>
              <Included on={ent.limits.custom_columns}>Custom board columns</Included>
              <Included on={ent.limits.shared_filters}>Shared filters</Included>
              <Included on>
                {formatLimit(ent.limits.activity_history_days)} days of activity history
              </Included>
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Plans"
          description="One flat price per workspace. Each tier includes a bucket of seats — not a per-person charge."
          action={
            <div
              role="group"
              aria-label="Billing interval"
              className="inline-flex rounded-lg border border-ink-200 p-0.5 dark:border-ink-700"
            >
              {(['month', 'year'] as BillingInterval[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={interval === option}
                  onClick={() => setInterval(option)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition',
                    interval === option
                      ? 'bg-brand-600 text-white'
                      : 'text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100',
                  )}
                >
                  {option === 'month' ? 'Monthly' : 'Yearly · 2 months free'}
                </button>
              ))}
            </div>
          }
        />

        {checkout.error ? (
          <div className="px-5 pt-4">
            <Alert tone="danger">{messageFor(checkout.error)}</Alert>
          </div>
        ) : null}
        {!data.can_checkout && data.blocked_reason && data.blocked_reason !== 'not_owner' ? (
          <div className="px-5 pt-4">
            <Alert tone="info">
              {data.blocked_reason === 'apple_subscription_active'
                ? 'This workspace is already Pro through the App Store.'
                : 'Checkout is not available for this workspace right now.'}
            </Alert>
          </div>
        ) : null}

        {/* A personal workspace cannot have members at all — Members says so in
            as many words — and Unlimited adds NOTHING over Business for one
            person: it differs only in storage headroom a solo user will not
            reach.

            So Unlimited is not offered here. Upgrading is still very much
            offered — Free is capped at 1 project and 25 issues with no sprints,
            reports or MCP, so hiding it would trap a solo user rather than
            protect them. The plan a workspace is ALREADY on is always shown,
            whatever it is, so a current plan can never become invisible. */}
        <div className={cn('grid gap-4 p-5 sm:grid-cols-2', personalPlans.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
          {personalPlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              allPlans={personalPlans}
              isPersonal={workspace.isPersonal}
              interval={interval}
              currentPlan={ent.plan}
              seatsUsed={ent.member_count}
              canCheckout={data.can_checkout}
              pending={checkout.isPending && checkout.variables === plan.id}
              onChoose={() => checkout.mutate(plan.id)}
            />
          ))}
        </div>

        {!currentPlan && ent.pro ? (
          <div className="px-5 pb-5">
            <p className={cn('text-xs', mutedTextClass)}>
              This workspace is on {currentPlanName}, which is not sold here. Choosing a plan above
              replaces it.
            </p>
          </div>
        ) : null}
      </Card>
    </div>
  )
}

/** One entitlement line: present, or explicitly not. */
function Included({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <li className={cn('flex gap-2', on ? undefined : 'text-ink-400 dark:text-ink-500')}>
      <span aria-hidden="true" className={on ? 'text-brand-600 dark:text-brand-400' : ''}>
        {on ? '\u2713' : '\u2014'}
      </span>
      <span className={on ? mutedTextClass : undefined}>{children}</span>
      <span className="sr-only">{on ? ' (included)' : ' (not included)'}</span>
    </li>
  )
}

/**
 * What a plan means to a workspace of one.
 *
 * The stored taglines are written for teams — "A small team, one flat price",
 * "Twice the seats", "for a whole company" — and every one of them describes
 * something a personal workspace cannot have. These say what actually changes.
 */
function personalTagline(plan: { id: string; tagline?: string | null }): string {
  switch (plan.id) {
    case 'free':
      return 'One project and 25 issues, with AI on your own Claude account.'
    case 'standard':
      return 'Unlimited projects and issues, plus sprints, reports and MCP.'
    case 'business':
      return 'Everything in Standard, with an audit log and priority support.'
    default:
      return plan.tagline ?? ''
  }
}

/** Highlights that only describe seats say nothing to a workspace of one. */
function withoutSeatTalk(highlights: string[]): string[] {
  return highlights.filter((line) => !/\b(member|members|seat|seats)\b/i.test(line))
}

function PlanCard({
  plan,
  allPlans,
  interval,
  currentPlan,
  seatsUsed,
  canCheckout,
  pending,
  onChoose,
  isPersonal,
}: {
  plan: BillingPlanEntry
  allPlans: BillingPlanEntry[]
  interval: BillingInterval
  currentPlan: string
  seatsUsed: number
  canCheckout: boolean
  pending: boolean
  onChoose: () => void
  /** A workspace of one: seat counts describe something it cannot have. */
  isPersonal: boolean
}) {
  // `prices` and `highlights` are null on the Free plan — a nil Go slice, not an
  // empty one — so they are normalised before anything indexes into them.
  const prices = plan.prices ?? []
  const highlights = isPersonal ? withoutSeatTalk(plan.highlights ?? []) : (plan.highlights ?? [])
  const price = prices.find((entry) => entry.interval === interval) ?? prices[0]
  // Every price is shown per month, the way it is compared; a yearly price is
  // the annual charge spread over twelve, set against the monthly price.
  const monthlyPrice = prices.find((entry) => entry.interval === 'month')
  const yearly = price?.interval === 'year'
  const savingPercent =
    yearly && price && monthlyPrice && monthlyPrice.unit_amount > 0
      ? Math.round((1 - price.unit_amount / (monthlyPrice.unit_amount * 12)) * 100)
      : 0
  const isCurrent = plan.id === currentPlan
  const isFree = plan.id === 'free'
  // A tier the workspace has already outgrown cannot be a downgrade target:
  // buying it would immediately put the workspace over its own cap.
  const tooSmall = plan.seat_limit > 0 && seatsUsed > plan.seat_limit

  // Every paid tier now has unlimited seats, so seat capacity no longer orders
  // the ladder. The catalogue lists plans cheapest first, so position does.
  const currentIndex = allPlans.findIndex((entry) => entry.id === currentPlan)
  const isUpgrade = currentIndex === -1 || allPlans.findIndex((entry) => entry.id === plan.id) > currentIndex

  // "Choose Standard" said nothing about what would happen; naming the
  // direction does. The plan name stays OUT of the label: it is the card's own
  // heading two lines above, and repeating it overflowed a 124px button in the
  // four-column layout. The full phrasing lives on aria-label, so a screen
  // reader still hears which plan the button belongs to.
  const ctaLabel = isUpgrade ? 'Upgrade' : 'Switch'
  const ctaDescription = isUpgrade ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`

  const priceMissing = price ? !price.configured : true
  const ctaDisabled = !canCheckout || tooSmall || priceMissing

  return (
    <div
      className={cn(
        'flex flex-col rounded-card border p-4',
        plan.recommended
          ? 'border-brand-300 ring-1 ring-brand-200 dark:border-brand-800 dark:ring-brand-900'
          : 'border-ink-200 dark:border-ink-800',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{plan.name}</h3>
        {plan.recommended ? <Badge tone="brand">Recommended</Badge> : null}
        {isCurrent ? <Badge tone="success">Current</Badge> : null}
      </div>
      {/* The seat count is the headline for a team and noise for one person,
          who cannot add anybody. What changes for them is projects, issues and
          the features Free withholds. */}
      {isPersonal ? null : (
        <p className="mt-1 text-xs font-medium text-brand-700 dark:text-brand-300">
          {formatSeats(plan.seat_limit)}
        </p>
      )}
      <p className={cn('mt-1 text-xs leading-relaxed', mutedTextClass)}>
        {isPersonal ? personalTagline(plan) : plan.tagline}
      </p>

      <p className="mt-4 flex flex-wrap items-baseline gap-x-1.5">
        {!isFree && yearly && monthlyPrice ? (
          <span className={cn('text-sm line-through', mutedTextClass)}>
            {formatMoney(monthlyPrice.unit_amount, monthlyPrice.currency)}
          </span>
        ) : null}
        <span className="text-2xl font-semibold tracking-tight">
          {isFree
            ? 'Free'
            : yearly && price
              ? formatMoney(Math.round(price.unit_amount / 12), price.currency)
              : (price?.display ?? formatMoney(price?.unit_amount ?? 0, price?.currency))}
        </span>
        {!isFree && price ? <span className={cn('text-xs', mutedTextClass)}>/mo</span> : null}
        {savingPercent > 0 ? <Badge tone="success">Save {savingPercent}%</Badge> : null}
      </p>
      {!isFree && price ? (
        <p className={cn('mt-0.5 text-[11px]', mutedTextClass)}>
          {yearly ? `Billed ${formatMoney(price.unit_amount, price.currency)} yearly` : 'Billed monthly'} · whole
          workspace, not per person
        </p>
      ) : null}

      <ul className="mt-4 flex-1 space-y-1.5 text-xs">
        {highlights.slice(0, 5).map((highlight) => (
          <li key={highlight} className="flex gap-2">
            <span aria-hidden="true" className="mt-0.5 text-brand-600 dark:text-brand-400">
              ✓
            </span>
            <span className={mutedTextClass}>{highlight}</span>
          </li>
        ))}
      </ul>

      {/* A fixed footer slot so every card ends at the same height, whatever
          state it is in. */}
      <div className="mt-4">
        {isCurrent ? (
          // A disabled button reading "Current plan" was a dead grey block that
          // looked broken, and it repeated the Current badge already in the
          // header. State belongs in the card; the only real action left is
          // managing the subscription, which lives above.
          <p className="flex h-10 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
            {isFree ? 'Your current plan' : "You're on this plan"}
          </p>
        ) : isFree ? (
          // Downgrading to Free is a cancellation, and Stripe owns that. Saying
          // so is honest; a "Choose Free" button opening checkout for a $0 plan
          // would not be.
          <p className={cn('flex h-10 items-center justify-center px-2 text-center text-xs', mutedTextClass)}>
            Cancel in the billing portal to return to Free.
          </p>
        ) : (
          <Button
            className="w-full"
            variant={plan.recommended ? 'primary' : 'secondary'}
            disabled={ctaDisabled}
            loading={pending}
            onClick={onChoose}
            aria-label={ctaDescription}
            title={ctaDescription}
          >
            {ctaLabel}
          </Button>
        )}
      </div>

      {!isFree && tooSmall && !isCurrent ? (
        <p className="mt-2 text-[11px] text-ink-400">
          This workspace already has {seatsUsed} members, more than this tier holds.
        </p>
      ) : null}
      {!isFree && price && !price.configured ? (
        <p className="mt-2 text-[11px] text-ink-400">
          Not purchasable on this deployment — no price is configured.
        </p>
      ) : null}
    </div>
  )
}
